import React from "react";

import { DraggableChipList } from "shared-ui/global/ChipList/Draggable";
import { useServingAmountCallbacks } from "shared-ui/ServingAmount/utils";

import Chip from "./Chip";

import { SxProps } from "@mui/material";
import {
  getRedesignExpandedIngredientsHeight,
  LOADING_INGREDIENTS_HEIGHT,
} from "shared-ui/ServingAmount/IngredientsList";
import { FullServingAmountFragment } from "../../../types";
import { useIsFoodFormRedesign } from "../../FoodFormUi/FoodFormUiContext";
import { useServingIdToExpanded } from "./utils";

// Overridden by label in getChipProps
const servingAmountToChipName = (sa: FullServingAmountFragment): string => sa.serving.foodOrRecipe.name;

// Note: 40 equals chip height of theme.spacing(4) + marginTop of theme.spacing(1)
const foodChipHeight = 40;
// Note: 64 equals chip height of theme.spacing(7) + marginTop of theme.spacing(1)
const limitedAccessChipHeight = 64;
// Note: 18 is height of div containing Typograph variant='caption' and line-height: 1.5, 8 is theme.spacing(1)
const getRecipeChipHeight = (ingredientsCount: number) => (ingredientsCount + 1) * 24 + foodChipHeight;
// Redesign rows: mt(8) + py(16) + content. Must match rendered border-box + margin for useDraggable.
const redesignChipHeight = 52; // content driven by 28px controls
const redesignChipHeightWithMacros = 60; // name(20) + macros(16)

const getRedesignChipHeight = (baseHeight: number, ingredientsCount: number | null, isExpanded: boolean): number => {
  if (!isExpanded) {
    return baseHeight;
  }
  if (ingredientsCount === null) {
    return baseHeight + LOADING_INGREDIENTS_HEIGHT;
  }
  return baseHeight + getRedesignExpandedIngredientsHeight(ingredientsCount);
};

interface ServingAmountsEditChipListProps {
  servingAmounts: readonly FullServingAmountFragment[];
  onChange: (servingAmounts: readonly FullServingAmountFragment[]) => void;
  recipeIngredient: boolean;
  loadingChip?: React.ReactNode;
  sx?: SxProps;
  disabled?: boolean;
  displayMacros?: boolean;
}

const ServingAmountsEditChipList = ({
  servingAmounts,
  onChange,
  sx,
  recipeIngredient,
  loadingChip,
  disabled,
  displayMacros = true,
}: ServingAmountsEditChipListProps) => {
  const isRedesign = useIsFoodFormRedesign();
  const { servingIdToExpanded, onChangeExpanded, onSetIngredientsCount } = useServingIdToExpanded();
  const {
    onChangeOrder,
    onDecrementByDefaultAmount,
    onDelete,
    onIncrementByDefaultAmount,
    onSetAmount,
    onReplaceServing,
    onDeconstructRecipe,
    sortedServingAmounts,
  } = useServingAmountCallbacks({ servingAmounts, onChange });

  // Hack level: over 9000
  // Explanation: This key forces a re-render when the servingAmounts change (including order)
  // Without it, the dragging of chips does not work
  const draggableChipListKey = sortedServingAmounts
    .flatMap(({ serving: { id } }) => {
      const { isExpanded, ingredientsCount } = servingIdToExpanded(id);
      return [id, isExpanded, ingredientsCount];
    })
    .join(",");

  return (
    <DraggableChipList<FullServingAmountFragment>
      loadingChip={loadingChip}
      key={draggableChipListKey}
      objects={[...sortedServingAmounts]}
      objectToName={servingAmountToChipName}
      renderChip={(sa, bindProps, spring) => (
        <Chip
          disabled={disabled}
          key={sa.serving.id}
          servingAmount={sa}
          onIncrement={() => onIncrementByDefaultAmount(sa)}
          onDecrement={() => onDecrementByDefaultAmount(sa)}
          onDelete={() => onDelete(sa)}
          onReplaceServing={(serving, amount) => onReplaceServing(sa, serving, amount)}
          onSetAmount={amount => onSetAmount(sa, amount)}
          expanded={servingIdToExpanded(sa.serving.id).isExpanded}
          onChangeExpanded={expanded => onChangeExpanded(sa.serving.id, expanded)}
          onLoadIngredients={count => onSetIngredientsCount(sa.serving.id, count)}
          onDeconstructRecipe={sa.serving.foodOrRecipe.__typename === "Recipe" ? () => onDeconstructRecipe(sa) : undefined}
          bindProps={bindProps}
          spring={spring}
          recipeIngredient={recipeIngredient}
          displayMacros={displayMacros}
        />
      )}
      sx={sx}
      chipHeight={0}
      objectToChipHeight={sa => {
        const expandedData = servingIdToExpanded(sa.serving.id);
        if (isRedesign) {
          const baseHeight = displayMacros ? redesignChipHeightWithMacros : redesignChipHeight;
          const canExpand = sa.serving.foodOrRecipe.__typename === "Recipe" && sa.serving.foodOrRecipe.hasFullAccess;
          return getRedesignChipHeight(baseHeight, expandedData.ingredientsCount, canExpand && expandedData.isExpanded);
        }
        if (!sa.serving.foodOrRecipe.hasFullAccess) {
          return limitedAccessChipHeight;
        }
        if (!expandedData.isExpanded) {
          return foodChipHeight;
        }
        if (expandedData.ingredientsCount === null) {
          return foodChipHeight + LOADING_INGREDIENTS_HEIGHT;
        }
        return getRecipeChipHeight(expandedData.ingredientsCount);
      }}
      onChangeOrder={onChangeOrder}
    />
  );
};

export default ServingAmountsEditChipList;
